// © Microsoft Corporation. All rights reserved.

import { appEnvironment, Environment } from "@msnews/core";

import {
    CreateSubscriptionParams, ManageSubscriptionParams, LinkToPublisherParams,
    GetSubscriptionsForPublisherParams, GetSubscriptionsForPublisherResponse,
    RenewSubscriptionParams, GetSubscriptionsParams, GetSubscriptionsResponse,
    UnlinkPublisherParams, SubscriptionSDKInitParams
} from "./Interfaces";
import { getSubscriptionContainerId, sdkLoaded } from "./Initialization";

export async function init (params: SubscriptionSDKInitParams) {
    return callSdkFunction("init", params);
}

export async function renderCreateSubscription (params: CreateSubscriptionParams) {
    return callSdkFunction("renderCreateSubscription", params);
}

export async function renderManageSubscription (params: ManageSubscriptionParams) {
    return callSdkFunction("renderManageSubscription", params);
}

export async function renewSubscription (params: RenewSubscriptionParams) {
    return callSdkFunction("renewSubscription", params);
}

export async function renderLinkToPublisher (params: LinkToPublisherParams) {
    return callSdkFunction("renderLinkToPublisher", params);
}

export async function getSubscriptionsForPublisher (params: GetSubscriptionsForPublisherParams): Promise<GetSubscriptionsForPublisherResponse> {
    return callSdkFunction("getSubscriptionsForPublisher", params);
}

export async function getSubscriptions (params: GetSubscriptionsParams): Promise<GetSubscriptionsResponse> {
    return callSdkFunction("getSubscriptions", params);
}

export async function unlinkPublisher (params: UnlinkPublisherParams): Promise<Boolean> {
    return callSdkFunction("unlinkPublisher", params);
}

export enum ProductType {
    Basic = 0,
    Advanced = 1,
    Premium = 2
}

export enum MsnProductType {
    Undefined = 0,
    Basic = 1,
    Advanced = 2,
    Premium = 3
}

export function mapSdkProductTypeToMsnProductType(sdkProductType: ProductType) {
    return sdkProductType + 1;
}

export function mapMsnProductTypeToSdkProductType(msnProductType: MsnProductType) {
    return msnProductType - 1;
}

export enum EnvList {
    PROD = 0,
    CI = 1,
    TEST = 2,
    INT = 3
}

async function callSdkFunction (functionName: string, params) {
    await sdkLoaded;
    const func = window.xpaySubscription[functionName];
    params = await ensureRequiredParams(params);
    return func(params);
}

async function ensureRequiredParams (params) {
    return {
        ...params,
        env: params.env !== undefined ? params.env : getSdkEnv(),
        tileContainerId: getSubscriptionContainerId()
    };
}

export function getSdkEnv (): EnvList {
    switch (appEnvironment.Environment) {
        case Environment.int:
        case Environment.int1:
        case Environment.intpr:
        case Environment.intpef:
            return EnvList.INT;
        case Environment.prod:
        default:
            return EnvList.PROD;
    }
}

export enum SubscriptionStatus {
    Active = "Active",
    Expired = "Expired",
    Canceled = "Canceled",
    PaymentFailed = "PaymentFailed"
}
